import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { computed } from 'mobx'
import type { DraftJob } from '@/features/jobs/drafting/DraftJob'
import type { QuestionnaireViewModelFactory } from '@/features/jobs/drafting/questionnaire/QuestionnaireViewModel'
import {
  OutlineViewModel,
  type StepAccessor,
} from '@/features/jobs/drafting/OutlineViewModel'
import { task } from 'mobx-task'
import { ignoreAbortError } from '@/utils/abort-util'
import { toStream } from '@/utils/mobx-util'
import { fromAbortSignal, shareLatest, withAbortSignal } from '@/utils/rx-util'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type CheckoutViewModelFactory } from '@/features/jobs/drafting/checkout/CheckoutViewModel'
import type { DraftPersister } from '@/features/jobs/drafting/DraftPersister'
import type { SnackbarFactory } from '@/features/shell'
import {
  EMPTY,
  exhaustMap,
  firstValueFrom,
  merge,
  type Observable,
  of,
  switchMap,
} from 'rxjs'
import { find, takeUntil, tap } from 'rxjs/operators'
import { extractMessageFromError } from '@/utils/error-util'
import type { RequestedProviderSelectionViewModelFactory } from '@/features/jobs/drafting/requested-provider/RequestedProviderSelectionViewModel'
import type { FetchLatestPublishedLegendVersionFn } from '@/features/jobs/internal'
import type { GetCurrentWorkspaceFn } from '@/features/iam'

/**
 * View-model for a draft job.
 */
export class DraftJobViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly outlineVm
  readonly questionnaireVm
  readonly requestedProviderSelectionViewModel
  readonly checkoutVm
  readonly stateSlices: DraftStateSlice[]
  readonly outlineModalVm = new ModalViewModel()

  /**
   * Whether the VM has prepared.
   */
  readonly isPrepared$ = toStream(() => this.prepare.resolved, true).pipe(
    shareLatest(),
  )

  /**
   * Modal for displaying the call center number.
   */
  readonly callSalesRepModalVm = new ModalViewModel()

  constructor(
    public readonly job: DraftJob,
    private readonly _persister: DraftPersister,
    private readonly _fetchLatestPublishedLegendVersion: FetchLatestPublishedLegendVersionFn,
    createQuestionnaireViewModel: QuestionnaireViewModelFactory,
    createRequestedProviderSelectionViewModel: RequestedProviderSelectionViewModelFactory,
    createCheckoutViewModel: CheckoutViewModelFactory,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _getCurrentWorkspace: GetCurrentWorkspaceFn,
  ) {
    this.outlineVm = new OutlineViewModel(
      this.isPrepared$,
      () => this.stepAccessors,
    )
    this._lifecycle.manage(this.outlineVm)

    this.questionnaireVm = createQuestionnaireViewModel(
      () => this.isReadOnly,
      this.outlineVm.createStep,
    )

    this.requestedProviderSelectionViewModel =
      createRequestedProviderSelectionViewModel(
        () => this.isReadOnly,
        this.outlineVm.createStep,
      )

    this.checkoutVm = createCheckoutViewModel({
      createStep: this.outlineVm.createStep,
      isReadyForCheckout: () => this.isReadyForCheckout,
      reloadSummary$: this._persister.persisted$,
    })

    // List of all state view-models.
    this.stateSlices = [
      this.questionnaireVm,
      this.requestedProviderSelectionViewModel,
      this.checkoutVm,
    ]

    for (const slice of this.stateSlices) {
      this._lifecycle.manage(slice)
    }

    // Wire up persistence.
    this._lifecycle.manage(
      merge(
        this.questionnaireVm.changes$,
        this.requestedProviderSelectionViewModel.changes$,
      ).pipe(tap((change) => this._persister.enqueue(change))),
    )

    // Wire up "continue" forwarding
    this._lifecycle.manage(
      merge(...this.stateSlices.map((s) => s.continue$ ?? EMPTY)).pipe(
        // Call `Continue` when requested, but not while already running.
        exhaustMap(() => (this.continue.resolved ? this.continue() : EMPTY)),
      ),
    )

    // Show snackbars on error.
    this._lifecycle.manage(
      merge(
        this.questionnaireVm.fetchError$,
        this._persister.persistError$,
      ).pipe(
        tap((err) =>
          _newSnackbar((s) =>
            s
              .type('error')
              .saying(err.title, extractMessageFromError(err.error))
              .show(),
          ),
        ),
      ),
    )

    // If there's a persistence error, reload the state slices
    // to make sure there's no unsaved state still visible.
    this._lifecycle.manage(
      this._persister.persistError$.pipe(
        switchMap(() => withAbortSignal((signal) => this._prepareCore(signal))),
      ),
    )
  }

  /**
   * All step accessors from all state VMs.
   */
  @computed get stepAccessors(): StepAccessor[] {
    const accessors: StepAccessor[] = []

    outer: for (const vm of this.stateSlices) {
      for (const accessor of vm.stepAccessors) {
        accessors.push(accessor)
        if (accessor.step.isShortCircuited) {
          break outer
        }
      }
    }

    return accessors
  }

  /**
   * Whether the interface is read-only.
   */
  @computed get isReadOnly() {
    return this.continue.pending || this.checkoutVm.submit.pending
  }

  /**
   * Whether the Continue button is visible.
   */
  @computed get isContinueVisible() {
    return !this.questionnaireVm.isBreakerActive
  }

  /**
   * The currently active slice.
   */
  @computed get activeSlice() {
    return (
      this.stateSlices.find((s) =>
        s.stepAccessors.some((a) => a.step.isActive),
      ) ?? null
    )
  }

  /**
   * Whether the job is ready for checkout.
   */
  @computed get isReadyForCheckout() {
    return this.questionnaireVm.isQuestionnaireCompleted
  }

  /**
   * Whether the call center is enabled.
   */
  @computed
  get callCenterEnabled() {
    return this._getCurrentWorkspace().config.callCenter.enabled
  }

  /**
   * The call center number to display in the modal.
   */
  @computed
  get callCenterNumber() {
    return this._getCurrentWorkspace().config.callCenter.phoneNumber
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()

    void this.prepare(this._lifecycle.signal).catch(ignoreAbortError())
  }

  /** @inheritDoc */
  deactivate() {
    this.outlineModalVm.close()
    this._lifecycle.deactivate()
    this.prepare.reset()
  }

  /**
   * Prepares all state slice view-models.
   */
  prepare = task(async (signal?: AbortSignal) => {
    // Check whether we need to upgrade the Legend version.
    // It's not common, so we can start preparing in the meantime. Worst case
    // we'll run it again after the upgrade.
    const [legendUpgraded] = await Promise.all([
      this._tryUpgradeLegend(signal),
      this._prepareCore(signal),
    ])

    signal?.throwIfAborted()

    if (legendUpgraded) {
      // Prepare again now that the Legend has been upgraded.
      await this._prepareCore(signal)
    }
  })

  /**
   * Continues to the next step when done saving.
   */
  continue = task.resolved(async () => {
    const signal = this._lifecycle.signal

    if (!this.isContinueVisible) {
      return
    }

    this.activeSlice?.flush?.()

    // Validate the active step.
    if (this.outlineVm.activeStep?.validate() === false) {
      return
    }

    const success = await this._persister.waitUntilFlushed()

    if (!success) {
      return
    }

    // Wait for the current slice to finish whatever it's doing.
    await firstValueFrom(
      (this.activeSlice?.isStallingContinue$ ?? of(false)).pipe(
        find((isStalling) => !isStalling),
        takeUntil(fromAbortSignal(signal)),
      ),
      { defaultValue: false },
    )

    if (signal.aborted) {
      return
    }

    if (this.outlineVm.activeStep?.position.last) {
      const firstIncompleteStep = this.outlineVm.stepAccessors.find(
        (a) => !a.step.isCompleted,
      )
      if (firstIncompleteStep) {
        this._newSnackbar((s) =>
          s
            .saying('Please complete all required steps')
            .type('warning')
            .show()
            .autoDismiss()
            .until(fromAbortSignal(signal)),
        )
        firstIncompleteStep.step.becomeActive('Backward')
      }

      return
    }

    this.outlineVm.goToNext()
  })

  /**
   * Prepares all state slice view-models.
   */
  private async _prepareCore(signal?: AbortSignal) {
    await Promise.all(this.stateSlices.map((s) => s.prepare(signal)))
    signal?.throwIfAborted()
  }

  /**
   * Attempts to upgrade the Legend to the latest available version
   * if needed.
   *
   * @private
   */
  private async _tryUpgradeLegend(signal?: AbortSignal): Promise<boolean> {
    const latest = await this._fetchLatestPublishedLegendVersion(
      this.job.legend.id,
    )

    if (!latest) {
      return false
    }

    if (latest.version > this.job.legend.version) {
      signal?.throwIfAborted()
      return await this._persister.enqueue({ type: 'UpgradeLegendVersion' })
    }

    return false
  }
}

/**
 * A slice of the draft job view-model's state.
 */
export interface DraftStateSlice extends ActivationLifecycle {
  /**
   * Step accessors for the slice of the state.
   */
  readonly stepAccessors: StepAccessor[]

  /**
   * Allows the slice to request that we continue to the next step.
   */
  readonly continue$?: Observable<void>

  /**
   * Allows the slice to stall the Continue action.
   * If this is implemented on the slice, the Continue action will
   * be stalled until this emits `false`.
   *
   * Useful for slices that asynchronously recalculate the available
   * steps. For example, questions could be refetched, revealing new questions,
   * but if the new questions query hasn't finished by the time we hit Continue,
   * the new questions won't be considered as possible "next steps".
   */
  readonly isStallingContinue$?: Observable<boolean>

  /**
   * Called by the draft view-model immediately after activation to
   * load relevant data for the slice.
   *
   * @param signal
   */
  prepare(signal?: AbortSignal): Promise<unknown>

  /**
   * If defined, gives the slice a chance to flush any pending changes to the
   * persister.
   */
  flush?(): void
}

/**
 * Emitted by state slices when there are errors.
 */
export interface DraftingError {
  /**
   * Title to use for the snackbar.
   */
  title: string
  /**
   * The actual error.
   */
  error: unknown
}
